import styled from "@emotion/styled";
import { IconButton, Tooltip } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandIcon from "@mui/icons-material/Expand";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { Typography, useBreakpoint } from "@avenue-8/sidekick-ui";
import { useChatWidgetLogic } from "@shared/hooks/useChatWidgetLogic";
import { Fragment, useState } from "react";

export type ChatWidgetNavbarAction = {
  key: string;
  icon?: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  grouped?: boolean;
  // When provided, renders this node directly in the grouped menu
  // instead of a standard icon + label MenuItem
  component?: (closeMenu: () => void) => React.ReactNode;
};

type Props = {
  children: React.ReactNode;
  actions?: Array<ChatWidgetNavbarAction>;
  subtitle?: string;
};

const ChatWidgetNavbarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  padding: 1rem;
  height: 4rem;
  background: ${(p) => p.theme.colors.background};
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
  position: sticky;
  top: 0;
  z-index: 200;
  will-change: transform;

  ${(p) => p.theme.media.laptopDown} {
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    padding: 0 0.5rem;
    display: grid;
    gap: 0.5rem;
    grid-template-columns: auto 1fr auto;
    justify-content: center;
  }
`;

const ChatWidgetNavbarOffset = styled.div`
  height: 4rem;

  ${(p) => p.theme.media.laptopDown} {
    height: 4rem;
  }
`;

const LeftActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RightActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
  flex: 1;
`;

const ActionItem = styled(IconButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
`;

const ReadOnlyItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavCenter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`;

export const ChatWidgetNavbar = (props: Props) => {
  const { children, actions, subtitle } = props;
  const { toggleChatWidgetDrawer, resetChatWidget } = useChatWidgetLogic();
  const isLaptopDown = useBreakpoint("laptop");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getHandleGroupedActionClick = (onClick?: () => void) => {
    return () => {
      handleMenuClose();
      if (onClick) {
        onClick();
      }
    };
  };

  const groupedActions =
    actions?.filter((action) => action && action.grouped) || [];
  const notGroupedActions =
    actions?.filter((action) => action && !action.grouped) || [];

  const groupedMenu = groupedActions.length > 0 && (
    <Menu
      anchorEl={anchorEl}
      open={menuOpen}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      {groupedActions.map((action) => {
        if (action.component) {
          return (
            <Fragment key={action.key}>
              {action.component(handleMenuClose)}
            </Fragment>
          );
        }
        if (action.readOnly) {
          return (
            <MenuItem key={action.key} disabled>
              {action.icon}{" "}
              <Typography variant="overline" fontWeight={700}>
                {action.label}
              </Typography>
            </MenuItem>
          );
        }
        return (
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            onClick={getHandleGroupedActionClick(action.onClick)}
          >
            {action.icon}{" "}
            <Typography variant="overline" fontWeight={700}>
              {action.label}
            </Typography>
          </MenuItem>
        );
      })}
    </Menu>
  );

  const renderNotGroupedActions = (useIndex = false) =>
    notGroupedActions.map((action, index) => {
      if (action.readOnly) {
        return (
          <ReadOnlyItem key={useIndex ? index : action.key}>
            {action.icon}
          </ReadOnlyItem>
        );
      }
      return (
        <Tooltip key={useIndex ? index : action.key} title={action.label}>
          <span>
            <ActionItem
              onClick={action.onClick}
              disabled={action.disabled}
              size="small"
            >
              {action.icon}
            </ActionItem>
          </span>
        </Tooltip>
      );
    });

  if (isLaptopDown) {
    return (
      <>
        <ChatWidgetNavbarContainer>
          <LeftActionContainer>
            <ActionItem onClick={resetChatWidget}>
              <ArrowBackOutlinedIcon />
            </ActionItem>
          </LeftActionContainer>
          <NavCenter>
            {children}
            {subtitle && (
              <Typography variant="caption" noWrap>
                {subtitle}
              </Typography>
            )}
          </NavCenter>
          {actions && (
            <RightActionsContainer>
              {renderNotGroupedActions(true)}
              {groupedActions.length > 0 && (
                <ActionItem onClick={handleMenuOpen}>
                  <MoreVertOutlinedIcon />
                </ActionItem>
              )}
            </RightActionsContainer>
          )}
        </ChatWidgetNavbarContainer>
        {isLaptopDown && <ChatWidgetNavbarOffset />}
        {groupedMenu}
      </>
    );
  }

  // Desktop view
  return (
    <>
      <ChatWidgetNavbarContainer>
        <LeftActionContainer>
          <IconButton onClick={() => toggleChatWidgetDrawer("collapsed")}>
            <ExpandIcon sx={{ transform: "rotate(90deg)" }} />
          </IconButton>
        </LeftActionContainer>
        {children}
        {actions && actions.length > 0 && (
          <RightActionsContainer>
            {renderNotGroupedActions()}
            {groupedActions.length > 0 && (
              <ActionItem onClick={handleMenuOpen} size="small">
                <MoreVertOutlinedIcon />
              </ActionItem>
            )}
          </RightActionsContainer>
        )}
      </ChatWidgetNavbarContainer>
      {groupedMenu}
    </>
  );
};
